import { useRef, useState } from "react";
import {
  UploadSimple,
  CircleNotch,
  FilePdf,
  Image as ImageIcon,
  Images,
  X,
} from "@phosphor-icons/react";
import { uploadFile } from "@/lib/api";
import MediaPickerModal from "./MediaPickerModal";

/**
 * Campo upload file per il pannello admin.
 * Props:
 *   - kind: 'image' | 'pdf'
 *   - label: string
 *   - value: URL corrente (string | null)
 *   - onChange: (url: string | null) => void
 *   - hint: opzionale, testo di aiuto (es. dimensione max)
 */
export default function FileUploadField({
  kind,
  label,
  value,
  onChange,
  hint,
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);

  const accept =
    kind === "image" ? "image/jpeg,image/png,image/webp" : "application/pdf";

  const handleFile = async (file) => {
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const res = await uploadFile(file, kind);
      onChange(res.url);
    } catch (e) {
      setError(e.message || "Errore upload");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const clear = () => {
    onChange(null);
    setError("");
  };

  const isImage = kind === "image";
  const filename = value ? value.split("/").pop() : null;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs uppercase tracking-[0.15em] font-bold text-[#1A1918]">
          {label}
        </label>
        {hint && <span className="text-[10px] text-[#5C5A55]">{hint}</span>}
      </div>

      {value ? (
        <div className="bg-[#FAF9F6] border border-[#D1CFC7] p-3 flex items-center gap-3">
          {isImage ? (
            <img
              src={value}
              alt="Anteprima"
              className="w-16 h-16 object-cover flex-shrink-0 border border-[#D1CFC7]"
            />
          ) : (
            <div className="w-16 h-16 bg-[#F0EFEB] flex items-center justify-center flex-shrink-0 border border-[#D1CFC7]">
              <FilePdf size={28} weight="duotone" className="text-[#EB0D0D]" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-[0.15em] font-bold text-[#5C5A55] mb-1">
              {isImage ? "Immagine" : "PDF"} caricato
            </div>
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-[#EB0D0D] hover:underline break-all block truncate"
              title={filename}
            >
              {filename}
            </a>
          </div>
          <div className="flex gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              disabled={uploading}
              className="p-2 hover:bg-[#F0EFEB] text-[#1A1918] transition-colors disabled:opacity-50"
              title="Scegli dalla libreria"
            >
              <Images size={16} weight="bold" />
            </button>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="p-2 hover:bg-[#F0EFEB] text-[#1A1918] transition-colors disabled:opacity-50"
              title="Carica un nuovo file"
            >
              {uploading ? (
                <CircleNotch size={16} weight="bold" className="animate-spin" />
              ) : (
                <UploadSimple size={16} weight="bold" />
              )}
            </button>
            <button
              type="button"
              onClick={clear}
              disabled={uploading}
              className="p-2 hover:bg-[#EB0D0D]/10 text-[#EB0D0D] transition-colors disabled:opacity-50"
              title="Rimuovi"
            >
              <X size={16} weight="bold" />
            </button>
          </div>
        </div>
      ) : (
        <div>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="w-full bg-[#FAF9F6] border-2 border-dashed border-[#D1CFC7] hover:border-[#EB0D0D] hover:bg-[#F0EFEB] transition-colors p-6 flex flex-col items-center justify-center gap-2 disabled:opacity-50"
          >
            {uploading ? (
              <>
                <CircleNotch
                  size={24}
                  weight="bold"
                  className="animate-spin text-[#EB0D0D]"
                />
                <span className="text-sm text-[#5C5A55]">Caricamento…</span>
              </>
            ) : (
              <>
                {isImage ? (
                  <ImageIcon
                    size={24}
                    weight="duotone"
                    className="text-[#5C5A55]"
                  />
                ) : (
                  <FilePdf size={24} weight="duotone" className="text-[#5C5A55]" />
                )}
                <span className="text-sm text-[#1A1918]">
                  Carica {isImage ? "immagine" : "PDF"}
                </span>
                <span className="text-[10px] text-[#5C5A55] uppercase tracking-[0.15em]">
                  clicca per selezionare
                </span>
              </>
            )}
          </button>
          <div className="mt-2 flex items-center justify-center gap-2 text-xs text-[#5C5A55]">
            <span>oppure</span>
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              disabled={uploading}
              className="inline-flex items-center gap-1.5 text-[#EB0D0D] font-bold uppercase tracking-[0.15em] text-[11px] hover:underline disabled:opacity-50"
            >
              <Images size={14} weight="bold" />
              Scegli dalla libreria
            </button>
          </div>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={(e) => handleFile(e.target.files?.[0])}
        className="hidden"
      />

      {error && (
        <div className="mt-2 p-2 bg-[#EB0D0D]/8 text-[#C00A0A] text-xs border border-[#EB0D0D]/30">
          {error}
        </div>
      )}

      {pickerOpen && (
        <MediaPickerModal
          kind={kind}
          onSelect={(url) => {
            onChange(url);
            setError("");
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}
